import React, { createContext, useContext, ReactNode } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../config/authConfig';
 
// Define the base URL from environment or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Create axios instance with default headers
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials: true, // Include credentials for all requests
});
 
// Types
interface APIContextType {
  generateResponse: (model_name: string, input_text: string, user_id: string, options?: {
    generate_image?: boolean;
    conversation_id?: string;
    document?: File;
    documents?: File[];
    project_instructions?: string;
  }, mode?: 'internal' | 'external') => Promise<any>;
  getModels: () => Promise<any>;
  getConversations: () => Promise<any>;
  getConversation: (conversationId: string) => Promise<any>;
  deleteConversation: (conversationId: string) => Promise<any>;
  uploadAttachment: (file: File) => Promise<any>;
  deleteAttachment: (attachmentId: string) => Promise<any>;
  bingGroundingSearch: (query: string, model: string, conversation_id: string, user_id: string) => Promise<any>;
  getChatHistory: (user_id: string, page?: number, limit?: number, mode?: 'internal' | 'external') => Promise<any>;
  getChatById: (chatId: string, user_id: string, mode?: 'internal' | 'external') => Promise<any>;
  createProject: (
    user_id: string,
    name: string,
    goal: string,
    instructions?: string
  ) => Promise<any>;
  uploadProjectDocument: (
    projectId: string,
    user_id: string,
    document_name: string,
    document: File
  ) => Promise<any>;
  getProjects: (user_id: string) => Promise<any>;
  getProjectDetails: (projectId: string, user_id: string) => Promise<any>;
  generateProjectResponse: (
    projectId: string,
    model_name: string,
    input_text: string,
    user_id: string,
    conversation_id?: string,
    options?: {
      documents?: File[];
      generate_image?: boolean;
    }
  ) => Promise<any>;
  updateProjectInstructions: (
    projectId: string,
    user_id: string,
    instructions: string
  ) => Promise<any>;
  shareConversation: (conversationId: string, mode?: 'internal' | 'external') => Promise<any>;
  // New v2 API methods
  v2Chat: (message: string, thread_id?: string) => Promise<any>;
  v2ChatStream: (message: string, thread_id?: string) => Promise<any>;
  externalChatStream: (input_text: string, model_name: string, conversation_id?: string, options?: {
    generate_image?: boolean;
    documents?: File[];
  }) => Promise<any>;
  v2NewChat: () => Promise<any>;
  v2GetChatSessions: () => Promise<any>;
  v2GetChatSession: (sessionId: string) => Promise<any>;
  v2SwitchChatSession: (sessionId: string) => Promise<any>;
  v2DeleteChatSession: (sessionId: string) => Promise<any>;
  testStreaming: () => Promise<void>;
  testSimpleStream: () => Promise<void>;
}
 
// Create context
const APIContext = createContext<APIContextType | undefined>(undefined);
 
// Provider component
export const APIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance } = useMsal();
 
  // Function to refresh token using MSAL
  const refreshToken = async () => {
    try {
      const accounts = instance.getAllAccounts();
     
      if (accounts.length > 0) {
        // MSAL will automatically handle token refresh
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
 
        if (tokenResponse.accessToken) {
          // Store new token in both sessionStorage and cookies
          localStorage.setItem('access_token', tokenResponse.accessToken);
          Cookies.set('access_token', tokenResponse.accessToken, { expires: 1/24 });
          return tokenResponse.accessToken;
        }
      }
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };
 
  // Add request interceptor to include access token
  api.interceptors.request.use((config) => {
    // Get token from cookies or sessionStorage
    const token = Cookies.get('access_token') || localStorage.getItem('access_token');
   
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
   
    return config;
  });
 
  // Add response interceptor to handle token expiration
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
 
      // If error is 401 and we haven't tried to refresh token yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
 
        try {
          // Try to refresh the token using MSAL
          const newToken = await refreshToken();
         
          if (newToken) {
            // Update the authorization header
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            // Retry the original request
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
        }
 
        // If refresh failed, clear tokens and redirect to login
        Cookies.remove('access_token');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
     
      return Promise.reject(error);
    }
  );
 
  // Generate response from the API (supports both internal and external modes)
  const generateResponse = async (model_name: string, input_text: string, user_id: string, options?: {
    generate_image?: boolean;
    conversation_id?: string;
    document?: File;
    documents?: File[];
    project_instructions?: string;
  }, mode: 'internal' | 'external' = 'external') => {
    try {
      // For internal mode, throw error - should use v2Chat API directly
      if (mode === 'internal') {
        throw new Error('Internal mode should use v2Chat API directly, not generateResponse');
      }

      // For external mode, use existing API
      if (options?.document || options?.documents) {
        // Handle document upload case
        const formData = new FormData();
 
        if (options.documents) {
          // Handle multiple documents
          options.documents.forEach((doc, index) => {
            formData.append(`documents[]`, doc);
          });
        } else if (options.document) {
          // Handle single document (backward compatibility)
          formData.append('documents[]', options.document);
        }
 
        formData.append('input_text', input_text);
        formData.append('model_name', model_name);
        formData.append('user_id', user_id);
        if (options.conversation_id) {
          formData.append('conversation_id', options.conversation_id);
        }
        if (options.project_instructions) {
          formData.append('project_instructions', options.project_instructions);
        }
 
        // Remove the default Content-Type header for FormData
        const response = await api.post('/generate-response', formData, {
          headers: {
            'Content-Type': undefined, // Let the browser set the correct boundary
          },
        });
        return response.data;
      } else {
        // Handle regular JSON payload case
        const payload: any = {
          model_name,
          input_text,
          user_id,
          conversation_id: options?.conversation_id || ''
        };
 
        if (options?.generate_image) {
          payload.generate_image = "true";
        }
        if (options?.project_instructions) {
          payload.project_instructions = options.project_instructions;
        }
 
        const response = await api.post('/generate-response', payload);
        return response.data;
      }
    } catch (error) {
      console.error('Error generating response:', error);
      throw error;
    }
  };
 
  // Get available models
  const getModels = async () => {
    return [
      { id: 'gpt-4o', name: 'gpt-4o' },
      // { id: 'gpt-4o-mini', name: 'gpt-4o-mini' },
      // { id: 'o3-mini', name: 'o3-mini' }
    ];
  };
 
  // Get all conversations
  const getConversations = async () => {
    return [];
  };
 
  // Get a specific conversation
  const getConversation = async (conversationId: string) => {
    return null;
  };
 
  // Delete a conversation
  const deleteConversation = async (conversationId: string) => {
    try {
      const response = await api.delete(`/api/chats/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  };
 
  // Upload attachment
  const uploadAttachment = async (file: File) => {
    return { id: Date.now().toString(), filename: file.name };
  };
 
  // Delete attachment
  const deleteAttachment = async (attachmentId: string) => {
    return true;
  };
 
  // Bing Grounding Search
  const bingGroundingSearch = async (query: string, model: string, conversation_id: string, user_id: string) => {
    try {
      const response = await api.post('/api/bing-grounding', {
        query,
        model,
        conversation_id,
        user_id
      });
      return response.data;
    } catch (error) {
      console.error('Error with Bing Grounding Search:', error);
      throw error;
    }
  };
 
  // Get chat history (supports both modes)
  const getChatHistory = async (user_id: string, page = 1, limit = 10, mode: 'internal' | 'external' = 'external') => {
    if (mode === 'internal') {
      const response = await api.get('/api/v2/chat_sessions', 
        // {withCredentials: true}
      );
      return response.data;
    } else {
      const response = await api.get('/api/chats', {
        params: { user_id, page, limit }
      });
      return response.data;
    }
  };
 
  // Get chat by ID (supports both modes)
  const getChatById = async (chatId: string, user_id: string, mode: 'internal' | 'external' = 'external') => {
    if (mode === 'internal') {
      const response = await api.get(`/api/v2/chat_sessions/${chatId}`, 
        //  {withCredentials: true}
        );
      return response.data;
    } else {
      const response = await api.get(`/api/chats/${chatId}`, {
        params: { user_id }
      });
      return response.data;
    }
  };
 
  // Create project
  const createProject = async (
    user_id: string,
    name: string,
    goal: string,
    instructions?: string
  ) => {
    const response = await api.post('/api/projects', {
      user_id,
      name,
      goal,
      instructions,
    });
    return response.data;
  };
 
  // Upload project document
  const uploadProjectDocument = async (
    projectId: string,
    user_id: string,
    document_name: string,
    document: File
  ) => {
    const formData = new FormData();
    formData.append('user_id', user_id);
    formData.append('document_name', document_name);
    formData.append('document', document);
 
    const response = await api.post(`/api/projects/${projectId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };
 
  // Get projects
  const getProjects = async (user_id: string) => {
    const response = await api.get(`/api/projects?user_id=${user_id}`);
    return response.data;
  };
 
  // Get project details
  const getProjectDetails = async (projectId: string, user_id: string) => {
    const response = await api.get(`/api/projects/${projectId}?user_id=${user_id}`);
    return response.data;
  };
 
  const generateProjectResponse = async (
    projectId: string,
    model_name: string,
    input_text: string,
    user_id: string,
    conversation_id?: string,
    options?: {
      documents?: File[];
      generate_image?: boolean;
    }
  ) => {
    try {
      // For file uploads, use FormData
      if (options?.documents && options.documents.length > 0) {
        const formData = new FormData();
 
        // Add documents
        options.documents.forEach((doc, index) => {
          formData.append(`documents[]`, doc);
        });
 
        // Add other parameters
        formData.append('input_text', input_text);
        formData.append('model_name', model_name);
        formData.append('user_id', user_id);
       
        if (projectId) {
          formData.append('conversation_id', projectId);
        }
 
        // Use the project-specific endpoint
        const response = await api.post(`/api/projects/${projectId}/conversation`, formData, {
          headers: {
            'Content-Type': undefined, // Let browser set correct boundary
          },
        });
       
        return response.data;
      } else {
        // Regular JSON request
        const payload: any = {
          user_id,
          model_name,
          input_text,
        };
 
        if (projectId) {
          payload.conversation_id = projectId;
        }
 
        if (options?.generate_image) {
          payload.generate_image = "true";
        }
 
        // Use the project-specific endpoint
        const response = await api.post(`/api/projects/${projectId}/conversation`, payload);
        return response.data;
      }
    } catch (error) {
      console.error('Error generating project response:', error);
      throw error;
    }
  };
 
 
  const updateProjectInstructions = async (
    projectId: string,
    user_id: string,
    instructions: string
  ) => {
    try {
      const response = await api.patch(`/api/projects/${projectId}`, {
        user_id,
        instructions
      });
      return response.data;
    } catch (error) {
      console.error('Error updating project instructions:', error);
      throw error;
    }
  };
 
  const shareConversation = async (conversationId: string, mode: 'internal' | 'external' = 'external') => {
    if (mode === 'internal') {
      const response = await api.post(`/api/v2/chat_sessions/${conversationId}/share`);
      return response.data;
    } else {
    const response = await api.post(`/api/chats/${conversationId}/share`);
    return response.data;
    }
  };

  // V2 API Methods (Internal Mode)
  const v2Chat = async (message: string) => {
    try {
      // Fetch session_id and thread_id from sessionStorage
      const session_id = sessionStorage.getItem('session_id');
      const thread_id = sessionStorage.getItem('thread_id');
 
      // Include them in the payload
      const payload = { message, session_id, thread_id };
 
      const response = await api.post('/v2/chat', payload, 
      //   {
      //   withCredentials: true
      // }
    );
 
      if (response.data?.session_id && response.data?.thread_id) {
        sessionStorage.setItem('session_id', response.data.session_id);
        sessionStorage.setItem('thread_id', response.data.thread_id);
      }
 
      return response.data;
    } catch (error) {
      console.error('Error with v2 chat:', error);
      throw error;
    }
  };
 
  const v2ChatStream = async (message: string, conversation_id?: string) => {
    try {
      const session_id = conversation_id || sessionStorage.getItem('session_id');
      const thread_id = sessionStorage.getItem('thread_id');
 
      const payload = { message, session_id, thread_id };
 
      const token = Cookies.get('access_token') || localStorage.getItem('access_token');
 
      const headers: any = {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      };
        
        if (token) {
          headers.Authorization = `Bearer ${token}`;
      }
      
      
      const response = await fetch(`${API_BASE_URL}/v2/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        // credentials: 'include',
      });
      
      
 
      if (!response.ok || !response.body) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
 
      // Clone the response stream for interception
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let firstChunkParsed = false;
 
      const interceptedStream = new ReadableStream({
        async start(controller) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
 
            const chunk = decoder.decode(value, { stream: true });
 
            // Handle only the first chunk to extract session_id and thread_id
            if (!firstChunkParsed) {
              try {
                // Extract JSON-like object from stream if your server sends it in first line
                const jsonStart = chunk.indexOf('{');
                const jsonEnd = chunk.indexOf('}') + 1;
                const jsonString = chunk.slice(jsonStart, jsonEnd);
 
                const data = JSON.parse(jsonString);
                if (data.session_id) sessionStorage.setItem('session_id', data.session_id);
                if (data.thread_id) sessionStorage.setItem('thread_id', data.thread_id);
                firstChunkParsed = true;
              } catch (err) {
                console.warn('Could not parse initial stream chunk:', chunk);
              }
            }
 
            controller.enqueue(value); // pass through
          }
 
          controller.close();
        }
      });
 
      const newResponse = new Response(interceptedStream, {
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
      });
 
      return newResponse;
    } catch (error) {
      console.error('Error with v2 chat stream:', error);
      throw error;
    }
  };
 
  const externalChatStream = async (input_text: string, model_name: string, conversation_id?: string, options?: {
    generate_image?: boolean;
    documents?: File[];
  }) => {
    try {
      const token = await refreshToken();
      if (!token) {
        throw new Error('No valid token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          input_text: input_text,
          model_name: model_name,
          conversation_id: conversation_id,
          generate_image: options?.generate_image || false,
          documents: options?.documents || []
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      return new ReadableStream({
        async start(controller) {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    controller.enqueue(data);
                  } catch (e) {
                    console.error('Error parsing stream data:', e);
                  }
                }
              }
            }
          } catch (error) {
            controller.error(error);
          } finally {
            controller.close();
          }
        }
      });
    } catch (error) {
      console.error('Error in externalChatStream:', error);
      throw error;
    }
  };

  const v2NewChat = async () => {
    try {
      const response = await api.post('/v2/new_chat', {});
     
      if (response.data?.session_id && response.data?.thread_id) {
        sessionStorage.setItem('session_id', response.data.session_id);
        sessionStorage.setItem('thread_id', response.data.thread_id);
        // Also set conversation_id for consistency
        sessionStorage.setItem('conversation_id', response.data.session_id);
      } else {
        console.warn('Missing session_id or thread_id in response:', response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Error creating v2 new chat:', error);
      throw error;
    }
  };

  const v2GetChatSessions = async () => {
    try {
      const response = await api.get('/api/v2/chat_sessions', 
      //   {
      //   withCredentials: true
      // }
    );
      return response.data;
    } catch (error) {
      console.error('Error getting v2 chat sessions:', error);
      throw error;
    }
  };

  const v2GetChatSession = async (sessionId: string) => {
    try {
      const response = await api.get(`/api/v2/chat_sessions/${sessionId}`, 
      //   {
      //   withCredentials: true
      // }
    );
      // Optionally store session/thread if needed
      if (response.data?.session?._id && response.data?.session?.azure_thread_id) {
        sessionStorage.setItem('session_id', response.data.session._id);
        sessionStorage.setItem('thread_id', response.data.session.azure_thread_id);
      }
      return response.data;
    } catch (error) {
      console.error('Error getting v2 chat session:', error);
      throw error;
    }
  };
 
  const v2SwitchChatSession = async (sessionId: string) => {
    try {
      const response = await api.post(`/api/v2/chat_sessions/${sessionId}/switch`, {}, 
      //   {
      //   withCredentials: true
      // }
    );
       // Store new session + thread ID
      if (response.data?.session_id && response.data?.thread_id) {
        sessionStorage.setItem('session_id', response.data.session_id);
        sessionStorage.setItem('thread_id', response.data.thread_id);
      }
      return response.data;
    } catch (error) {
      console.error('Error switching v2 chat session:', error);
      throw error;
    }
  };
 
  const v2DeleteChatSession = async (sessionId: string) => {
    try {
      const response = await api.delete(`/api/v2/chat_sessions/${sessionId}`, 
      //   {
      //   withCredentials: true
      // }
    );
      if (sessionId === sessionStorage.getItem('session_id')) {
        sessionStorage.removeItem('session_id');
        sessionStorage.removeItem('thread_id');
      }
      return response.data;
    } catch (error) {
      console.error('Error deleting v2 chat session:', error);
      throw error;
    }
  };
 
  // Test function for debugging streaming
  const testStreaming = async () => {
    try {
      const response = await v2ChatStream('Hello, test message');
      
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
        }
      }
    } catch (error: any) {
      console.error('Test streaming error:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data
      });
    }
  };

  // Test simple streaming endpoint
  const testSimpleStream = async () => {
    try {
      const token = Cookies.get('access_token') || localStorage.getItem('access_token');
      const headers: any = {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      };
      
        if (token) {
          headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/test-stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: 'Hello' }),
        // credentials: 'include',
      });
      
      
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
        }
      }
    } catch (error: any) {
      console.error('Simple test streaming error:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data
      });
    }
  };

  // Expose test function globally for debugging
  if (typeof window !== 'undefined') {
    (window as any).testStreaming = testStreaming;
    (window as any).testSimpleStream = testSimpleStream;
    (window as any).v2ChatStream = v2ChatStream;
    (window as any).v2Chat = v2Chat;
  }

  // Context value
  const value = {
    generateResponse,
    getModels,
    getConversations,
    getConversation,
    deleteConversation,
    uploadAttachment,
    deleteAttachment,
    bingGroundingSearch,
    getChatHistory,
    getChatById,
    createProject,
    uploadProjectDocument,
    getProjects,
    getProjectDetails,
    generateProjectResponse,
    updateProjectInstructions,
    shareConversation,
    v2Chat,
    v2ChatStream,
    externalChatStream,
    v2NewChat,
    v2GetChatSessions,
    v2GetChatSession,
    v2SwitchChatSession,
    v2DeleteChatSession,
    testStreaming,
    testSimpleStream
  };
 
  return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
};
 
// Custom hook for using the API context
export const useAPI = () => {
  const context = useContext(APIContext);
  if (context === undefined) {
    throw new Error('useAPI must be used within an APIProvider');
  }
  return context;
};